import React, { useState, useRef, useEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import { EventClickArg, DateSelectArg, EventDropArg } from '@fullcalendar/core';
import { PlusIcon, CalendarIcon, ClockIcon, ViewColumnsIcon, ListBulletIcon } from '@heroicons/react/24/outline';
import { Appointment, AppointmentFormData, CalendarViewType } from '../../types/crud/appointments';
import { useAppointments } from '../../hooks/useAppointments';
import { AppointmentModal } from './AppointmentModal';
import { toast } from 'sonner';
import { tratamientosApi } from '../../services/api';

export function AppointmentCalendar() {
  const calendarRef = useRef<FullCalendar>(null);
  const {
    appointments,
    patients,
    fisioterapeutas,
    isLoading,
    createAppointment,
    updateAppointment
  } = useAppointments();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();
  const [currentView, setCurrentView] = useState<CalendarViewType>('dayGridMonth');
  const [currentDate, setCurrentDate] = useState<Date>(() => new Date());
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return document.documentElement.classList.contains('dark') || 
           window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Efecto para escuchar cambios del tema
  useEffect(() => {
    const checkTheme = () => {
      const isDark = document.documentElement.classList.contains('dark') || 
                     window.matchMedia('(prefers-color-scheme: dark)').matches;
      setIsDarkMode(isDark);
    };

    // Observer para cambios en la clase dark del documentElement
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    // Listener para cambios en el tema del sistema
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', checkTheme);

    return () => {
      observer.disconnect();
      mediaQuery.removeEventListener('change', checkTheme);
    };
  }, [currentDate]);

  // Función para obtener colores del calendario según el tema
  const getCalendarColors = () => {
    if (isDarkMode) {
      return {
        eventBackgroundColor: '#60a5fa', // blue-400
        eventBorderColor: '#3b82f6', // blue-500
        eventTextColor: '#1f2937', // gray-800
      };
    } else {
      return {
        eventBackgroundColor: '#2563eb', // blue-600
        eventBorderColor: '#1d4ed8', // blue-700
        eventTextColor: '#ffffff', // white
      };
    }
  };

  // Calcular los colores una sola vez
  const calendarColors = getCalendarColors();

  // Convertir appointments a formato FullCalendar
  const calendarEvents = appointments.map(appointment => ({
    id: appointment.id,
    title: appointment.title,
    start: appointment.start,
    end: appointment.end,
    backgroundColor: appointment.backgroundColor,
    borderColor: appointment.borderColor,
    textColor: appointment.textColor,
    extendedProps: {
      appointment
    }
  }));

  // Efecto para posicionar el calendario en la fecha actual al cargar y cuando cambian las citas
  useEffect(() => {
    if (!isLoading && calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      const today = new Date();
      
      // Solo ir a la fecha actual si no se ha navegado manualmente
      if (Math.abs(currentDate.getTime() - today.getTime()) < 24 * 60 * 60 * 1000) {
        calendarApi.gotoDate(today);
        setCurrentDate(today);
      }
    }
  }, [isLoading, appointments]);

  // Efecto para manejar cambios de vista
  useEffect(() => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      
      // Listener para cuando cambia la fecha del calendario
      const handleDatesSet = (info: any) => {
        setCurrentDate(new Date(info.start));
      };
      
      calendarApi.on('datesSet', handleDatesSet);
      
      return () => {
        calendarApi.off('datesSet', handleDatesSet);
      };
    }
  }, []);

  const handleDateSelect = (selectInfo: DateSelectArg) => {
    setSelectedAppointment(null);
    setSelectedDate(selectInfo.start);
    setIsModalOpen(true);
  };

  const handleEventClick = (clickInfo: EventClickArg) => {
    const appointment = clickInfo.event.extendedProps.appointment as Appointment;
    setSelectedAppointment(appointment);
    setSelectedDate(undefined);
    setIsModalOpen(true);
  };

  const handleEventDrop = async (dropInfo: EventDropArg) => {
    const appointment = dropInfo.event.extendedProps.appointment as Appointment;
    
    try {
      await updateAppointment(appointment.id, {
        start: dropInfo.event.start!.toISOString(),
        end: dropInfo.event.end!.toISOString(),
        title: appointment.title,
        patientId: appointment.patientId,
        fisioterapeutaId: appointment.fisioterapeutaId,
        description: appointment.description,
        status: appointment.status,
        type: appointment.type,
        notes: appointment.notes
      });
      
      toast.success('Cita actualizada correctamente');
    } catch (error) {
      console.error('Error al actualizar la cita:', error);
      toast.error('Error al actualizar la cita');
      dropInfo.revert();
    }
  };

  const handleSaveAppointment = async (data: AppointmentFormData) => {
    try {
      if (selectedAppointment) {
        await updateAppointment(selectedAppointment.id, data);
        toast.success('Cita actualizada correctamente');
      } else {
        await createAppointment(data);
        toast.success('Cita creada correctamente');
      }
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error al guardar la cita:', error);
      toast.error('Error al guardar la cita');
      throw error;
    }
  };

  const handleViewChange = (view: CalendarViewType) => {
    setCurrentView(view);
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.changeView(view);
    }
  };

  const goToToday = () => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      const today = new Date();
      calendarApi.gotoDate(today);
      setCurrentDate(today);
      toast.success('Navegando a hoy');
    }
  };

  const goToPrevious = () => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.prev();
    }
  };

  const goToNext = () => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.next();
    }
  };

  const viewOptions = [
    { key: 'dayGridMonth', label: 'Mes', icon: CalendarIcon },
    { key: 'timeGridWeek', label: 'Semana', icon: ViewColumnsIcon },
    { key: 'timeGridDay', label: 'Día', icon: ClockIcon },
    { key: 'listWeek', label: 'Lista', icon: ListBulletIcon }
  ];

  // Función para obtener el título y contador contextual según la vista
  const getContextualInfo = () => {
    const today = new Date();
    let title = '';
    let count = 0;
    
    switch (currentView) {
      case 'dayGridMonth':
        // Vista mensual
        const monthYear = currentDate.toLocaleDateString('es-ES', { 
          month: 'long', 
          year: 'numeric' 
        });
        title = monthYear.charAt(0).toUpperCase() + monthYear.slice(1);
        
        // Contar citas del mes actual
        count = appointments.filter(apt => {
          const aptDate = new Date(apt.start);
          return aptDate.getMonth() === currentDate.getMonth() && 
                 aptDate.getFullYear() === currentDate.getFullYear();
        }).length;
        break;
        
      case 'timeGridWeek':
        // Vista semanal
        const startOfWeek = new Date(currentDate);
        startOfWeek.setDate(currentDate.getDate() - currentDate.getDay() + 1); // Lunes
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6); // Domingo
        
        if (startOfWeek.getMonth() === endOfWeek.getMonth()) {
          title = `Semana del ${startOfWeek.getDate()} al ${endOfWeek.getDate()} de ${startOfWeek.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}`;
        } else {
          title = `Semana del ${startOfWeek.getDate()} ${startOfWeek.toLocaleDateString('es-ES', { month: 'short' })} al ${endOfWeek.getDate()} ${endOfWeek.toLocaleDateString('es-ES', { month: 'short', year: 'numeric' })}`;
        }
        
        // Contar citas de la semana
        count = appointments.filter(apt => {
          const aptDate = new Date(apt.start);
          return aptDate >= startOfWeek && aptDate <= endOfWeek;
        }).length;
        break;
        
      case 'timeGridDay':
        // Vista diaria
        if (currentDate.toDateString() === today.toDateString()) {
          title = 'Hoy';
        } else {
          title = currentDate.toLocaleDateString('es-ES', { 
            weekday: 'long', 
            day: 'numeric', 
            month: 'long',
            year: 'numeric'
          });
          title = title.charAt(0).toUpperCase() + title.slice(1);
        }
        
        // Contar citas del día
        count = appointments.filter(apt => {
          const aptDate = new Date(apt.start);
          return aptDate.toDateString() === currentDate.toDateString();
        }).length;
        break;
        
      case 'listWeek':
        // Vista lista (semanal)
        const listStartOfWeek = new Date(currentDate);
        listStartOfWeek.setDate(currentDate.getDate() - currentDate.getDay() + 1);
        const listEndOfWeek = new Date(listStartOfWeek);
        listEndOfWeek.setDate(listStartOfWeek.getDate() + 6);
        
        title = `Lista semanal`;
        
        // Contar citas de la semana
        count = appointments.filter(apt => {
          const aptDate = new Date(apt.start);
          return aptDate >= listStartOfWeek && aptDate <= listEndOfWeek;
        }).length;
        break;
        
      default:
        title = 'Calendario';
        count = appointments.length;
    }
    
    return { title, count };
  };

  // Función para obtener el texto del botón de navegación
  const getNavigationButtonText = () => {
    switch (currentView) {
      case 'dayGridMonth':
        return 'Mes actual';
      case 'timeGridWeek':
      case 'listWeek':
        return 'Semana actual';
      case 'timeGridDay':
        return 'Hoy';
      default:
        return 'Actual';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600 dark:text-gray-400">Cargando citas...</span>
      </div>
    );
  }

  // Calcular información contextual una sola vez
  const contextualInfo = getContextualInfo();
  const navigationButtonText = getNavigationButtonText();

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
      {/* Header con controles */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6 gap-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsModalOpen(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Nueva Cita
            </button>
          </div>
          
  
        </div>

        {/* Navegación y controles */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
          {/* Navegación de fechas */}
          <div className="flex items-center gap-2">
            <button
              onClick={goToPrevious}
              className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
              title="Anterior"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            <button
              onClick={goToToday}
              className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              title="Ir a hoy"
            >
              {navigationButtonText}
            </button>
            
            <button
              onClick={goToNext}
              className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
              title="Siguiente"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Selector de vista */}
          <div className="flex items-center border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
            {viewOptions.map((option) => {
              const IconComponent = option.icon;
              return (
                <button
                  key={option.key}
                  onClick={() => handleViewChange(option.key as CalendarViewType)}
                  className={`px-3 py-2 text-sm font-medium flex items-center gap-2 transition-colors ${
                    currentView === option.key
                      ? 'bg-blue-600 text-white'
                      : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                  } border-r border-gray-300 dark:border-gray-600 last:border-r-0`}
                  title={option.label}
                >
                  <IconComponent className="h-4 w-4" />
                  <span className="hidden sm:inline">{option.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Calendario */}
      <div className="calendar-container">
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
          initialView={currentView}
          initialDate={new Date()}
          headerToolbar={
            {
              left: '',
              center: 'title',
              right: '',
            }
          }
          events={appointments}
          selectable={true}
          selectMirror={true}
          dayMaxEvents={true}
          weekends={true}
          editable={true}
          droppable={true}
          select={handleDateSelect}
          eventClick={handleEventClick}
          eventDrop={handleEventDrop}
          height="auto"
          locale="es"
          timeZone="local"
          slotMinTime="07:00:00"
          slotMaxTime="22:00:00"
          slotDuration="00:30:00"
          slotLabelInterval="01:00:00"
          allDaySlot={false}
          businessHours={{
            daysOfWeek: [1, 2, 3, 4, 5, 6],
            startTime: '08:00',
            endTime: '20:00'
          }}
          eventTimeFormat={{
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          }}
          slotLabelFormat={{
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          }}
          dayHeaderFormat={{ 
            weekday: 'short'
          }}
          buttonText={{
            today: 'Hoy',
            month: 'Mes',
            week: 'Semana',
            day: 'Día',
            list: 'Lista'
          }}
          nowIndicator={true}
          eventDisplay="block"
          displayEventTime={true}
          eventBackgroundColor={calendarColors.eventBackgroundColor}
          eventBorderColor={calendarColors.eventBorderColor}
          eventTextColor={calendarColors.eventTextColor}
          moreLinkText={(num) => `+${num} más`}
          weekNumbers={false}
          firstDay={1}
          dayMaxEventRows={3}
          eventMaxStack={3}
          validRange={{
            start: '2020-01-01',
            end: '2030-12-31'
          }}
          // Configuraciones específicas para vista lista
          listDayFormat={{
            weekday: 'long',
            day: 'numeric',
            month: 'long'
          }}
          listDaySideFormat={{
            weekday: 'short',
            day: 'numeric'
          }}
          noEventsText="No hay citas programadas"
        />
      </div>

      {/* Modal */}
      <AppointmentModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedAppointment(null);
          setSelectedDate(undefined);
        }}
        onSave={handleSaveAppointment}
        appointment={selectedAppointment}
        patients={patients}
        fisioterapeutas={fisioterapeutas}
        initialDate={selectedDate}
      />
    </div>
  );
} 